import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateRangePreset, ChartGranularity } from '../../../../models/chart.models';

export interface DateRangeValue {
  startDate: string;
  endDate: string;
  preset: DateRangePreset;
}

/**
 * Date range picker with preset options and granularity selector
 */
@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="date-range-picker">
      <!-- Preset Buttons -->
      <div class="preset-buttons">
        <button 
          *ngFor="let preset of presets"
          (click)="selectPreset(preset.value)"
          [class.active]="selectedPreset === preset.value"
          class="preset-btn"
          type="button">
          {{ preset.label }}
        </button>
      </div>
      
      <!-- Custom Date Range -->
      <div class="custom-dates" [class.visible]="selectedPreset === 'custom'">
        <div class="date-input-group">
          <label>From</label>
          <input 
            type="date" 
            [(ngModel)]="startDate"
            (change)="onCustomDateChange()"
            class="date-input">
        </div>
        <div class="date-input-group">
          <label>To</label>
          <input 
            type="date" 
            [(ngModel)]="endDate"
            (change)="onCustomDateChange()"
            class="date-input">
        </div>
      </div>
      
      <!-- Granularity Selector -->
      <div *ngIf="showGranularity" class="granularity-selector">
        <label>Group By:</label>
        <select [(ngModel)]="selectedGranularity" (ngModelChange)="onGranularityChange()" class="granularity-select">
          <option *ngFor="let g of granularityOptions" [ngValue]="g.value">{{ g.label }}</option>
        </select>
      </div>
    </div>
  `,
  styles: [`
    .date-range-picker {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
    }
    
    .preset-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }
    
    .preset-btn {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      border: 1px solid #e5e7eb;
      background: white;
      color: #374151;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    
    .preset-btn:hover {
      background: #f9fafb;
      border-color: #d1d5db;
    }
    
    .preset-btn.active {
      background: #10b981;
      border-color: #10b981;
      color: white;
    }
    
    .custom-dates {
      display: none;
      gap: 1rem;
      align-items: flex-end;
    }
    
    .custom-dates.visible {
      display: flex;
    }
    
    .date-input-group {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    
    .date-input-group label {
      font-size: 0.75rem;
      color: #6b7280;
      font-weight: 500;
    }
    
    .date-input {
      padding: 0.5rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      color: #374151;
    }
    
    .date-input:focus {
      outline: none;
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }
    
    .granularity-selector {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;
    }
    
    .granularity-selector label {
      font-size: 0.875rem;
      color: #6b7280;
    }
    
    .granularity-select {
      padding: 0.5rem 2rem 0.5rem 0.75rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      color: #374151;
      background: white;
      cursor: pointer;
      min-width: 120px;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
      background-position: right 0.5rem center;
      background-repeat: no-repeat;
      background-size: 1.5em 1.5em;
      position: relative;
      z-index: 10;
    }
    
    .granularity-select:focus {
      outline: none;
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }
    
    .granularity-select option {
      padding: 0.5rem;
      background: white;
      color: #374151;
    }
    
    @media (max-width: 768px) {
      .date-range-picker {
        flex-direction: column;
        align-items: stretch;
      }
      
      .preset-buttons {
        justify-content: flex-start;
      }
      
      .granularity-selector {
        margin-left: 0;
      }
    }
  `]
})
export class DateRangePickerComponent implements OnInit {
  @Input() showGranularity: boolean = true;
  @Input() initialPreset: DateRangePreset = 'last30days';
  @Input() initialGranularity: ChartGranularity = ChartGranularity.Daily;
  
  @Output() dateRangeChange = new EventEmitter<DateRangeValue>();
  @Output() granularityChange = new EventEmitter<ChartGranularity>();

  presets: { value: DateRangePreset; label: string }[] = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisQuarter', label: 'This Quarter' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom' }
  ];

  granularityOptions: { value: ChartGranularity; label: string }[] = [
    { value: ChartGranularity.Daily, label: 'Daily' },
    { value: ChartGranularity.Weekly, label: 'Weekly' },
    { value: ChartGranularity.Monthly, label: 'Monthly' }
  ];

  selectedPreset: DateRangePreset = 'last30days';
  selectedGranularity: ChartGranularity = ChartGranularity.Daily;
  startDate: string = '';
  endDate: string = '';

  ngOnInit(): void {
    this.selectedPreset = this.initialPreset;
    this.selectedGranularity = this.initialGranularity;
    this.applyPreset(this.selectedPreset);
  }

  selectPreset(preset: DateRangePreset): void {
    this.selectedPreset = preset;
    if (preset !== 'custom') {
      // Auto-adjust granularity for longer date ranges
      this.autoAdjustGranularity(preset);
      this.applyPreset(preset);
    }
  }

  /**
   * Automatically adjust granularity based on date range preset
   */
  private autoAdjustGranularity(preset: DateRangePreset): void {
    const previousGranularity = this.selectedGranularity;
    
    switch (preset) {
      case 'thisQuarter':
      case 'thisYear':
        // Use Monthly for long date ranges
        this.selectedGranularity = ChartGranularity.Monthly;
        break;
      case 'last30days':
      case 'thisMonth':
      case 'lastMonth':
        // Use Weekly for medium date ranges
        this.selectedGranularity = ChartGranularity.Weekly;
        break;
      case 'last7days':
      case 'today':
      case 'yesterday':
        // Use Daily for short date ranges
        this.selectedGranularity = ChartGranularity.Daily;
        break;
      default:
        // Keep current granularity for custom
        break;
    }
    
    // Emit granularity change if it changed
    if (previousGranularity !== this.selectedGranularity) {
      this.granularityChange.emit(this.selectedGranularity);
    }
  }

  onCustomDateChange(): void {
    if (this.startDate && this.endDate) {
      this.emitDateRange();
    }
  }

  onGranularityChange(): void {
    this.granularityChange.emit(this.selectedGranularity);
  }

  private applyPreset(preset: DateRangePreset): void {
    const { startDate, endDate } = this.getDateRangeFromPreset(preset);
    this.startDate = this.formatDateForInput(startDate);
    this.endDate = this.formatDateForInput(endDate);
    this.emitDateRange();
  }

  private emitDateRange(): void {
    this.dateRangeChange.emit({
      startDate: this.startDate,
      endDate: this.endDate,
      preset: this.selectedPreset
    });
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private getDateRangeFromPreset(preset: DateRangePreset): { startDate: Date; endDate: Date } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return { startDate: today, endDate: today };
      
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { startDate: yesterday, endDate: yesterday };
      }
      
      case 'last7days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { startDate: start, endDate: today };
      }
      
      case 'last30days': {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { startDate: start, endDate: today };
      }
      
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: start, endDate: today };
      }
      
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { startDate: start, endDate: end };
      }
      
      case 'thisQuarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        return { startDate: start, endDate: today };
      }
      
      case 'thisYear': {
        const start = new Date(now.getFullYear(), 0, 1);
        return { startDate: start, endDate: today };
      }
      
      default:
        return { startDate: today, endDate: today };
    }
  }
}

